// =============================================================================
// clientHoursReportExport.ts — T-370 / T-458
// Two exports for a Client Hours Report (client + month, grouped by asset owner
// or carrier):
//   • Excel = INTERNAL review — ACTUAL (worked) vs REPORTABLE (what we send),
//     both days and hours, with the full project + employee breakdown.
//   • PDF   = CLIENT report — clean: per group + per project, the REPORTABLE
//     days and hours, with each employee's reportable days under the project.
// Reportable figures are edited per person in the builder before sending.
// pdfmake loader mirrors diaryPdf.ts / quotePdf.ts.
// =============================================================================

import * as XLSX from "xlsx";
import { dated } from "@/lib/exportCsv";

// @ts-ignore — Vite ?url suffix isn't in pdfmake's type declarations
import pdfmakeScriptUrl from "pdfmake/build/pdfmake.min.js?url";
// @ts-ignore
import vfsFontsScriptUrl from "pdfmake/build/vfs_fonts.js?url";

export interface HoursGroup {
  group_name:     string;
  actual_days:    number;
  reported_days:  number;
  actual_hours:   number;
  reported_hours: number;
  note?:          string | null;
}
export interface HoursDetail {
  group_name:     string;
  project_code:   string;
  project_name:   string;
  employee_name:  string;
  actual_days:    number;
  reported_days:  number;
  actual_hours:   number;
  reported_hours: number;
}
export interface HoursReportExport {
  clientName:   string;
  periodLabel:  string;
  periodStart:  string;
  periodEnd:    string;
  groupByLabel: string;
  groups:       HoursGroup[];
  detail:       HoursDetail[];
  notes?:       string | null;
}

const N = (n: number) => Math.round(Number(n) * 100) / 100;
const sumAH = (g: HoursGroup[]) => N(g.reduce((a, x) => a + Number(x.actual_hours || 0), 0));
const sumRH = (g: HoursGroup[]) => N(g.reduce((a, x) => a + Number(x.reported_hours || 0), 0));
const sumAD = (g: HoursGroup[]) => g.reduce((a, x) => a + Number(x.actual_days || 0), 0);
const sumRD = (g: HoursGroup[]) => g.reduce((a, x) => a + Number(x.reported_days || 0), 0);
const safeName = (s: string) => s.replace(/[^\w]+/g, "_");

function detailByGroup(r: HoursReportExport): Map<string, HoursDetail[]> {
  const m = new Map<string, HoursDetail[]>();
  for (const d of r.detail) {
    if (!m.has(d.group_name)) m.set(d.group_name, []);
    m.get(d.group_name)!.push(d);
  }
  return m;
}

// Recompute group aggregates from the per-person detail rows so the summary can
// NEVER disagree with the breakdown (the detail rows are the source of truth —
// they're what the director edits per person). Notes carried over by group name.
function rollupGroups(r: HoursReportExport): HoursGroup[] {
  const notes = new Map<string, string | null | undefined>();
  for (const g of r.groups) notes.set(g.group_name, g.note);
  const m = new Map<string, HoursGroup>();
  for (const d of r.detail) {
    let g = m.get(d.group_name);
    if (!g) {
      g = { group_name: d.group_name, actual_days: 0, reported_days: 0, actual_hours: 0, reported_hours: 0, note: notes.get(d.group_name) ?? null };
      m.set(d.group_name, g);
    }
    g.actual_days += d.actual_days; g.reported_days += d.reported_days;
    g.actual_hours += d.actual_hours; g.reported_hours += d.reported_hours;
  }
  return Array.from(m.values())
    .map((g) => ({ ...g, actual_hours: N(g.actual_hours), reported_hours: N(g.reported_hours) }))
    .sort((a, b) => b.reported_hours - a.reported_hours);
}

// ---- Excel (INTERNAL — actual vs reportable, days + hours) -------------------

export function exportClientHoursXlsx(r: HoursReportExport): void {
  const wb = XLSX.utils.book_new();
  const grp = rollupGroups(r);

  const summary: (string | number)[][] = [
    ["Coretel Group — Hours report (INTERNAL)"],
    [`Client: ${r.clientName}`],
    [`Period: ${r.periodLabel}   ·   by ${r.groupByLabel}`],
    ["Internal review — ACTUAL worked vs REPORTABLE (what we send). Not for the client."],
    [],
    [r.groupByLabel, "Actual days", "Reported days", "Actual hrs", "Reported hrs", "Hrs variance", "Note"],
    ...grp.map((g) => [
      g.group_name, g.actual_days, g.reported_days, N(g.actual_hours), N(g.reported_hours),
      N(g.reported_hours - g.actual_hours), g.note ?? "",
    ]),
    [],
    ["TOTAL", sumAD(grp), sumRD(grp), sumAH(grp), sumRH(grp), N(sumRH(grp) - sumAH(grp)), ""],
  ];
  const ws1 = XLSX.utils.aoa_to_sheet(summary);
  ws1["!cols"] = [{ wch: 26 }, { wch: 11 }, { wch: 12 }, { wch: 10 }, { wch: 11 }, { wch: 12 }, { wch: 28 }];
  XLSX.utils.book_append_sheet(wb, ws1, "Summary");

  const rows: (string | number)[][] = [
    ["Breakdown — projects + employees (actual vs reportable)"],
    [`${r.clientName} · ${r.periodLabel}`],
    [],
    [r.groupByLabel, "Project", "Project name", "Employee", "Actual days", "Reported days", "Actual hrs", "Reported hrs"],
  ];
  for (const [group, dets] of detailByGroup(r)) {
    for (const d of dets) {
      rows.push([group, d.project_code, d.project_name, d.employee_name, d.actual_days, d.reported_days, N(d.actual_hours), N(d.reported_hours)]);
    }
  }
  const ws2 = XLSX.utils.aoa_to_sheet(rows);
  ws2["!cols"] = [{ wch: 20 }, { wch: 15 }, { wch: 26 }, { wch: 24 }, { wch: 11 }, { wch: 12 }, { wch: 10 }, { wch: 11 }];
  XLSX.utils.book_append_sheet(wb, ws2, "Breakdown");

  XLSX.writeFile(wb, `${dated(`hours_INTERNAL_${safeName(r.clientName)}`)}.xlsx`);
}

// ---- PDF (CLIENT — reportable days + hours) ---------------------------------

function loadScriptOnce(src: string, tag: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[data-pdfmake-tag="${tag}"]`);
    if (existing) {
      if (existing.dataset.loaded === "true") resolve();
      else {
        existing.addEventListener("load", () => resolve());
        existing.addEventListener("error", () => reject(new Error(`Failed to (re)load ${tag}`)));
      }
      return;
    }
    const s = document.createElement("script");
    s.src = src; s.async = true; s.dataset.pdfmakeTag = tag;
    s.onload = () => { s.dataset.loaded = "true"; resolve(); };
    s.onerror = () => reject(new Error(`Failed to load ${tag}`));
    document.head.appendChild(s);
  });
}
async function getPdfMake(): Promise<any> {
  const existing = (window as any).pdfMake;
  if (existing?.vfs && Object.keys(existing.vfs).length > 0) return existing;
  await loadScriptOnce(pdfmakeScriptUrl, "pdfmake-core");
  await loadScriptOnce(vfsFontsScriptUrl, "pdfmake-vfs");
  const p = (window as any).pdfMake;
  if (!p) throw new Error("pdfMake missing after load");
  return p;
}

const C = { primary: "#ff7844", ink: "#111", muted: "#666", rule: "#ececea", head: "#3a3a3a", sub: "#e9e9e6" };

export async function downloadClientHoursPdf(r: HoursReportExport): Promise<void> {
  const pdfMake = await getPdfMake();
  const grp = rollupGroups(r);
  const total = sumRH(grp);

  const summaryBody = [
    [
      { text: r.groupByLabel, style: "th" },
      { text: "On-site days", style: "th", alignment: "right" },
      { text: "Hours", style: "th", alignment: "right" },
    ],
    ...grp.map((g, i) => ([
      { text: g.group_name, style: "td", fillColor: i % 2 ? "#f6f6f4" : null },
      { text: String(g.reported_days), style: "td", alignment: "right", fillColor: i % 2 ? "#f6f6f4" : null },
      { text: N(g.reported_hours).toString(), style: "td", alignment: "right", fillColor: i % 2 ? "#f6f6f4" : null },
    ])),
    [
      { text: "TOTAL", style: "tdBold" },
      { text: String(sumRD(grp)), style: "tdBold", alignment: "right" },
      { text: N(total).toString(), style: "tdBold", alignment: "right" },
    ],
  ];

  const breakdown: any[] = [];
  for (const [group, dets] of detailByGroup(r)) {
    const pm = new Map<string, { code: string; name: string; hours: number; emps: { name: string; days: number }[] }>();
    for (const d of dets) {
      const k = `${d.project_code}|${d.project_name}`;
      let e = pm.get(k);
      if (!e) { e = { code: d.project_code, name: d.project_name, hours: 0, emps: [] }; pm.set(k, e); }
      e.hours += d.reported_hours;
      e.emps.push({ name: d.employee_name, days: d.reported_days });
    }
    const projects = Array.from(pm.values()).sort((a, b) => b.hours - a.hours);
    const body: any[] = [[
      { text: "Site / project · employee", style: "th2" },
      { text: "Days", style: "th2", alignment: "right" },
      { text: "Hours", style: "th2", alignment: "right" },
    ]];
    for (const p of projects) {
      body.push([
        { text: p.name ? `${p.code} — ${p.name}` : p.code, style: "td2", bold: true },
        { text: "", style: "td2" },
        { text: N(p.hours).toString(), style: "td2", bold: true, alignment: "right" },
      ]);
      for (const e of p.emps.sort((a, b) => a.name.localeCompare(b.name))) {
        body.push([
          { text: e.name, style: "td2", margin: [16, 2.5, 3, 2.5], color: C.muted },
          { text: String(e.days), style: "td2", alignment: "right", color: C.muted },
          { text: "", style: "td2" },
        ]);
      }
    }
    breakdown.push({ text: group, bold: true, fontSize: 10.5, color: C.ink, margin: [0, 10, 0, 3] });
    breakdown.push({
      table: { headerRows: 1, widths: ["*", 42, 55], body },
      layout: { hLineColor: () => C.rule, vLineColor: () => C.rule, hLineWidth: () => 0.5, vLineWidth: () => 0 },
    });
  }

  const doc: any = {
    pageSize: "A4",
    pageMargins: [40, 54, 40, 46],
    defaultStyle: { font: "Roboto", fontSize: 10, color: C.ink },
    content: [
      {
        columns: [
          [
            { text: "Coretel Group Pty Ltd", bold: true, fontSize: 13, color: C.primary },
            { text: "Hours report", fontSize: 9, color: C.muted, margin: [0, 1, 0, 0] },
          ],
          { text: r.periodLabel, alignment: "right", fontSize: 9, color: C.muted, margin: [0, 4, 0, 0] },
        ],
      },
      { canvas: [{ type: "line", x1: 0, y1: 6, x2: 515, y2: 6, lineWidth: 1, lineColor: C.rule }], margin: [0, 4, 0, 10] },
      { text: r.clientName, bold: true, fontSize: 12, margin: [0, 0, 0, 2] },
      { text: `Hours by ${r.groupByLabel.toLowerCase()} · ${r.periodStart} to ${r.periodEnd}`, fontSize: 9.5, color: C.muted, margin: [0, 0, 0, 12] },
      {
        table: { headerRows: 1, widths: ["*", 90, 70], body: summaryBody },
        layout: { hLineColor: () => C.rule, vLineColor: () => C.rule, hLineWidth: (i: number) => (i === 1 ? 1 : 0.5), vLineWidth: () => 0 },
      },
      ...(r.notes ? [{ text: r.notes, fontSize: 9, color: C.muted, margin: [0, 12, 0, 0] }] : []),
      { text: "Breakdown", bold: true, fontSize: 11, color: C.primary, margin: [0, 18, 0, 0] },
      ...breakdown,
    ],
    styles: {
      th: { bold: true, color: "#fff", fillColor: C.head, fontSize: 9, margin: [4, 4, 4, 4] },
      td: { fontSize: 10, margin: [4, 4, 4, 4] },
      tdBold: { fontSize: 10, bold: true, margin: [4, 5, 4, 5] },
      th2: { bold: true, fontSize: 8.5, color: C.muted, fillColor: C.sub, margin: [3, 3, 3, 3] },
      td2: { fontSize: 9, margin: [3, 2.5, 3, 2.5] },
    },
    footer: (page: number, count: number) => ({
      columns: [
        { text: "coretelgroup.com", fontSize: 8, color: C.muted, margin: [40, 0, 0, 0] },
        { text: `${page} / ${count}`, alignment: "right", fontSize: 8, color: C.muted, margin: [0, 0, 40, 0] },
      ],
      margin: [0, 12, 0, 0],
    }),
  };

  pdfMake.createPdf(doc).download(`${dated(`hours_${safeName(r.clientName)}`)}.pdf`);
}
